/**
 * features/shell/AppShell.tsx — โครงหน้าหลัก (sidebar + header + เนื้อหา)
 */

import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { LogOut, Menu, Moon, Sun, User, X } from 'lucide-react';
import { useAuth } from '~/features/auth/auth-context';
import { useTheme } from '~/features/theme/use-theme';
import { Button } from '~/ui/primitives';
import { Sidebar } from './Sidebar';

export function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bg">
      {/* header */}
      <header
        className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-border-base bg-surface px-3"
        style={{ height: 'var(--header-height)' }}
      >
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            iconOnly
            size="sm"
            className="lg:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="เปิดเมนู"
          >
            <Menu size={18} />
          </Button>
          <span className="text-sm font-semibold tracking-tight">FT-Account</span>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            iconOnly
            size="sm"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'สลับเป็นธีมสว่าง' : 'สลับเป็นธีมมืด'}
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </Button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((current) => !current)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-hover"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
                <User size={14} />
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-xs font-medium">{user?.displayName || user?.username}</span>
              </span>
            </button>

            {userMenuOpen ? (
              <>
                {/* คลิกนอกเมนูเพื่อปิด */}
                <button
                  type="button"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setUserMenuOpen(false)}
                  aria-label="ปิดเมนู"
                  tabIndex={-1}
                />
                <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border border-border-base bg-surface py-1 shadow-md">
                  <div className="border-b border-border-base px-3 py-2">
                    <p className="truncate text-xs font-medium text-fg">
                      {user?.displayName || user?.username}
                    </p>
                    <p className="truncate text-xs text-fg-muted">
                      {user?.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้ใช้งาน'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void logout()}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-danger hover:bg-surface-hover"
                  >
                    <LogOut size={14} />
                    ออกจากระบบ
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </header>

      {/* sidebar เดสก์ท็อป */}
      <aside
        className="fixed inset-y-0 left-0 z-20 hidden border-r border-border-base bg-surface lg:block"
        style={{ width: 'var(--sidebar-width)', top: 'var(--header-height)' }}
      >
        <Sidebar />
      </aside>

      {/* sidebar มือถือ: overlay + drawer */}
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-[var(--overlay)]"
            onClick={() => setMobileNavOpen(false)}
            aria-label="ปิดเมนู"
          />
          <div className="absolute inset-y-0 left-0 w-64 bg-surface shadow-lg">
            <div className="flex items-center justify-between border-b border-border-base px-3 py-3">
              <span className="text-sm font-semibold">FT-Account</span>
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                onClick={() => setMobileNavOpen(false)}
                aria-label="ปิดเมนู"
              >
                <X size={18} />
              </Button>
            </div>
            <Sidebar onNavigate={() => setMobileNavOpen(false)} />
          </div>
        </div>
      ) : null}

      <main
        className="px-4 py-5 lg:pl-[calc(var(--sidebar-width)+1rem)]"
        style={{ paddingTop: 'calc(var(--header-height) + 1.25rem)' }}
      >
        <div className="mx-auto max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
